import { Link, type LinkProps } from '@tanstack/react-router';
import { StepDot, type StepState } from '@/components/ui/workspace/steps';
import { cn } from '@/lib/utils/cn';

/**
 * SubTabsNav — the horizontal sub-tab strip under a module heading
 * (C20-133, shared in T-0242). Moved as-is from Contract Compliance's
 * `ContractSubTabsNav` so Health & Readiness draws the same strip: each tab
 * a real router link, an amber underline on the active one.
 *
 * A tab with a `state` is a step read left to right (T-0272, E24-F01):
 * the `Steps` dot sits before its label, a tick when done, a lock while it
 * waits on an earlier step, amber on the active one. Every step stays a
 * link, so a locked one can say what it waits for.
 *
 * Visual contract: prototype `.tabs > .tab` (screens-modules-tabs.js
 * `subTabs()`) and `.hr2-tabs` (health-readiness-v2.css) — horizontal flex
 * with a 16px gap, 36px tabs, ink-500 → ink-700 on hover, ink-900 with an
 * accent-amber underline when active, a 1px ink-100 rule under the strip.
 */
export interface SubTab {
  /** What `active` matches. */
  key: string;
  label: string;
  to: LinkProps['to'];
  params?: LinkProps['params'];
  /** Present: the tab is a step, drawn with its numbered dot. */
  state?: Exclude<StepState, 'current'>;
}

export interface SubTabsNavProps {
  /** The strip's accessible name ("Contract Compliance sub-navigation"). */
  label: string;
  tabs: readonly SubTab[];
  active: string;
  className?: string;
}

export function SubTabsNav({ label, tabs, active, className }: SubTabsNavProps) {
  return (
    <nav aria-label={label} className={cn('border-b border-ink-100', className)} role="tablist">
      <ul className="flex flex-wrap items-center gap-4 px-0.5">
        {tabs.map((t, i) => {
          const isActive = t.key === active;
          return (
            <li key={t.key}>
              <Link
                to={t.to}
                params={t.params}
                role="tab"
                aria-selected={isActive}
                data-step-state={t.state}
                className={cn(
                  'inline-flex h-9 items-center gap-2 border-b-2 px-1 text-body-sm font-medium transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                  isActive
                    ? 'border-accent-amber-600 text-ink-900'
                    : 'border-transparent text-ink-500 hover:text-ink-700',
                )}
              >
                {t.state ? (
                  <StepDot
                    size="sm"
                    n={i + 1}
                    state={t.state === 'todo' && isActive ? 'current' : t.state}
                  />
                ) : null}
                {t.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
